import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Type declaration for fbq global
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

/**
 * Hook to track PageView on every route change in SPA
 * The initial PageView is tracked by the script in index.html
 * This hook tracks subsequent navigations
 */
export const useMetaPixel = () => {
  const location = useLocation();

  useEffect(() => {
    // Skip the initial render since index.html already tracks PageView
    // Track only on subsequent route changes
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);
};

/**
 * Utility function to track custom Meta Pixel events
 * 
 * @example
 * // Track a purchase
 * trackMetaEvent('Purchase', { value: 29.90, currency: 'BRL' });
 * 
 * // Track registration
 * trackMetaEvent('CompleteRegistration');
 * 
 * // Track lead
 * trackMetaEvent('Lead', { content_name: 'Free Trial' });
 */
export const trackMetaEvent = (
  eventName: string,
  params?: Record<string, unknown>
) => {
  if (typeof window.fbq === 'function') {
    if (params) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }
  }
};

/**
 * Standard Meta Pixel events for reference:
 * - PageView: Tracked automatically on navigation
 * - Purchase: When a purchase is made
 * - CompleteRegistration: When a user completes registration
 * - AddToCart: When adding items to cart
 * - InitiateCheckout: When starting checkout process
 * - Lead: When a user submits info (signup, trial)
 * - ViewContent: When viewing specific content
 * - Search: When using search
 * - AddPaymentInfo: When adding payment info
 */
